import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionManagement = ({ userId, activeSessions }) => {
  const [sessions, setSessions] = useState(activeSessions);

  const handleTerminateSession = (sessionId) => {
    setSessions((prev) => prev?.filter((s) => s?.id !== sessionId));
    console.log('Terminated session:', sessionId);
  };

  const handleTerminateAll = () => {
    setSessions([]);
    console.log('Terminated all sessions for user:', userId);
  };

  const formatDuration = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) return `${diffMins} minutes`;
    if (diffHours < 24) return `${diffHours} hours`;
    return `${Math.floor(diffHours / 24)} days`;
  };

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase()?.includes('mobile')) return 'Smartphone';
    if (device?.toLowerCase()?.includes('tablet')) return 'Tablet';
    return 'Monitor';
  };

  const getBrowserIcon = (browser) => {
    if (browser?.toLowerCase()?.includes('chrome')) return 'Chrome';
    if (browser?.toLowerCase()?.includes('firefox')) return 'Globe';
    if (browser?.toLowerCase()?.includes('safari')) return 'Compass';
    return 'Globe';
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Active Sessions
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {sessions?.length} active session{sessions?.length !== 1 ? 's' : ''}{' '}
              currently running
            </p>
          </div>
          {sessions?.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              iconName="XCircle"
              onClick={handleTerminateAll}
            >
              Terminate All
            </Button>
          )}
        </div>
      </div>
      <div className="p-6">
        {sessions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon
              name="MonitorOff"
              size={48}
              className="mx-auto text-muted-foreground mb-3"
            />
            <p className="text-sm text-muted-foreground">
              No active sessions found
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions?.map((session) => (
              <div
                key={session?.id}
                className="p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon
                        name={getDeviceIcon(session?.device)}
                        size={24}
                        className="text-primary"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-foreground">
                          {session?.device}
                        </h4>
                        {session?.isCurrent && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                            Current
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Icon name={getBrowserIcon(session?.browser)} size={14} />
                          <span>{session?.browser}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={14} />
                          <span>{session?.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Globe" size={14} />
                          <span className="font-data text-xs">
                            {session?.ipAddress}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={14} />
                          <span>
                            Active for {formatDuration(session?.startTime)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-muted-foreground">
                        Last activity:{' '}
                        {new Date(session.lastActivity)?.toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>

                  {!session?.isCurrent && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="XCircle"
                      onClick={() => handleTerminateSession(session?.id)}
                    >
                      Terminate
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon
              name="Shield"
              size={20}
              className="text-warning flex-shrink-0 mt-0.5"
            />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                Security Notice:
              </p>
              <p>
                If you notice any suspicious sessions, terminate them immediately
                and change your password. Sessions automatically expire after 24
                hours of inactivity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;